import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { FailedRequestBufferService } from './failed-request-buffer.service';

/**
 * Captures failed HTTP requests (and their X-Trace-Id response header) into the
 * FailedRequestBufferService so a later bug report can correlate the failure to
 * the matching server log.
 *
 * REGISTRATION ORDER (important): this interceptor MUST be registered LAST in the
 * HTTP_INTERCEPTORS list. Angular runs the request path in registration order but
 * the response/error path in REVERSE, so the last-registered interceptor's
 * catchError fires FIRST on the way back — guaranteeing we observe every error
 * before another interceptor can transform or swallow it.
 */
@Injectable()
export class CaptureHttpInterceptor implements HttpInterceptor {
    constructor(private _buffer: FailedRequestBufferService) {
    }

    public intercept(
            req: HttpRequest<any>,
            next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
                tap((event: HttpEvent<any>) => {
                    // Capture the trace id from successful responses too, so a bug
                    // report filed after a working call still surfaces a real id.
                    if (event instanceof HttpResponse) {
                        this._buffer.recordTraceId(event.headers?.get('X-Trace-Id') ?? null);
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    this._buffer.record({
                        method: req.method,
                        url: req.url,
                        status: error.status,
                        traceId: error.headers?.get('X-Trace-Id') ?? null,
                        timestamp: new Date().toISOString(),
                    });

                    // Re-throw unchanged — capture is observe-only and must not alter behavior.
                    return throwError(() => error);
                }),
        );
    }
}
